import React, { useEffect, useRef, useState } from "react";
import "./AboutMe.css";

const timelineItems = [
  {
    date: "September 2018",
    title: "Started Attending Redmond High School",
    description: "Began high school and started building the academic foundation that would later support my interest in technology, leadership, and personal growth."
  },
  {
    date: "January 2019",
    title: "Placed 2nd in the IAC Eitanim Hackathon Competition",
    description: "Earned 2nd place in the IAC Eitanim Hackathon, an early milestone that strengthened my confidence in problem solving, collaboration, and building technical solutions."
  },
  {
    date: "September 2020",
    title: "Started Running Start Part Time",
    description: "Began the Running Start program part time, taking on college-level coursework early and gaining experience balancing increased academic responsibility."
  },
  {
    date: "June 2021",
    title: "Received Presidential Volunteering Medal",
    description: "Received the Presidential Volunteering Medal, recognizing meaningful commitment to service, consistency, and giving back to the community."
  },
  {
    date: "September 2021",
    title: "Became Operations Director of Friends of the Israeli Scouts in Seattle",
    description: "Was selected by organization leaders to serve as Operations Director, developing leadership, communication, and organizational skills at an early stage."
  },
  {
    date: "June 2022",
    title: "Graduated from Redmond High School",
    description: "Completed high school and prepared to begin the next chapter academically and professionally."
  },
  {
    date: "August 2022",
    title: "Started Computer Science at Arizona State University",
    description: "Began pursuing a Bachelor of Science in Computer Science at Arizona State University, focusing on software engineering, data systems, and long-term technical growth."
  },
  {
    date: "December 2023",
    title: "Elected Communications Director of Zeta Beta Tau",
    description: "Was elected Communications Director of the Gamma Tau Chapter of Zeta Beta Tau, expanding experience in leadership, communication, and organizational visibility."
  },
  {
    date: "April 2024",
    title: "Received Brother of the Semester Honors",
    description: "Recognized by peers in the Gamma Tau chapter for contribution, leadership, and impact within the organization."
  },
  {
    date: "June 2024",
    title: "Selected as an Undergraduate Supreme Councilor",
    description: "Was selected by the international president of Zeta Beta Tau to become one of four undergraduate supreme councilors, a major leadership milestone."
  },
  {
    date: "July 2024",
    title: "Joined the Executive Board of Zeta Beta Tau",
    description: "Was voted in as the undergraduate representative on the executive board of Zeta Beta Tau, strengthening leadership experience at a national level."
  },
  {
    date: "August 2024",
    title: "Finance Intern at Arizona Athletic Grounds",
    description: "Worked as a Finance Intern, gaining hands-on experience with reporting, analysis, and business-focused data processes in a professional environment."
  },
  {
    date: "August 2025",
    title: "Started GM3 Solutions Soft-Skills AI Capstone",
    description: "Began working under GM3 Solutions through the Arizona State capstone program to help develop a soft-skills AI product."
  },
  {
    date: "March 2026",
    title: "Completed Business Minor",
    description: "Completed a Business minor with a 3.5 GPA, strengthening the business perspective I bring into technical problem solving."
  },
  {
    date: "May 2026",
    title: "Graduated from Arizona State University",
    description: "Graduated with a Bachelor of Science in Computer Science and began the next stage of my career in software engineering."
  },
  {
    date: "June 2026",
    title: "Started Internship with Nextiva as GTM AI Intern",
    description: "Began working at Nextiva as a GTM AI Intern, developing AI-driven workflows and automation systems using modern LLM tools and APIs to support go-to-market operations and business strategy."
  }
];

function RevealSection({ children, className = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting);
      },
      {
        threshold: 0.02,
        rootMargin: "0px 0px -40px 0px"
      }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <section
      ref={ref}
      className={`${className} ${visible ? "about-visible" : "about-hidden"}`}
    >
      {children}
    </section>
  );
}

export default function AboutMe() {
  return (
    <div className="about-container">
      <RevealSection className="about-hero glass-panel">
        <p className="about-kicker">About Me</p>
        <h1 className="about-title">My Story</h1>
        <p className="about-subtitle">
          A deeper look into my journey, experiences, leadership, personal life,
          and long-term vision.
        </p>
      </RevealSection>

      <RevealSection className="about-section">
        <div className="section-header">
          <h2 className="section-title">Timeline</h2>
          <p className="section-subtitle">
            A timeline of the milestones that have shaped my academic, leadership,
            and professional growth.
          </p>
        </div>

        <div className="timeline">
          {timelineItems.map((item, index) => (
            <div
              key={`${item.date}-${item.title}`}
              className={`timeline-item ${index % 2 === 0 ? "left" : "right"}`}
            >
              <div className="timeline-dot"></div>

              <div className="timeline-card glass-panel">
                <span className="timeline-date">{item.date}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </RevealSection>

      <RevealSection className="about-section">
        <div className="section-header">
          <h2 className="section-title">Professional Life</h2>
          <p className="section-subtitle">
            What I care about professionally and the type of technical work I enjoy building.
          </p>
        </div>

        <div className="about-card glass-panel">
          <p className="about-text">
            Professionally, I am focused on software engineering, data systems,
            and machine learning. I enjoy building practical applications that
            solve real-world problems and create a clear impact for the people
            using them. Through my coursework, projects, and internship
            experience, I have developed a strong interest in full-stack
            development, database-driven systems, and intelligent technologies
            that combine strong engineering with usability. My resume reflects
            a mix of technical depth and versatility, including experience with
            software projects, mobile app development, AI-based systems, and
            data-focused problem solving. I am especially motivated by work
            that allows me to build scalable systems, improve user experiences,
            and apply software to needs that people deal with every day.
          </p>
        </div>
      </RevealSection>

      <RevealSection className="about-section">
        <div className="section-header">
          <h2 className="section-title">Personal Life</h2>
          <p className="section-subtitle">
            The interests, values, and experiences that define who I am beyond academics and work.
          </p>
        </div>

        <div className="about-card glass-panel">
          <p className="about-text">
            Outside of academics and technology, a major part of my personal
            life has been shaped by leadership and brotherhood through Zeta Beta
            Tau. My ZBT journey has been one of the most meaningful parts of my
            college experience, giving me the opportunity to lead, represent my
            chapter, and build lasting connections with people around me. Beyond
            leadership, I enjoy sports, spending time in the gym, and staying
            highly committed to dieting and personal fitness. I also enjoy
            playing video games, creating programs that help with everyday
            needs, and finding ways to use technology in practical parts of life.
            Outside of that, I enjoy fishing, golfing, and getting outdoors.
            Altogether, these interests reflect a balance of competition,
            discipline, creativity, and a strong appreciation for both personal
            growth and everyday experiences.
          </p>
        </div>
      </RevealSection>

      <RevealSection className="about-section">
        <div className="section-header">
          <h2 className="section-title">Ambitions & Goals</h2>
          <p className="section-subtitle">
            The long-term vision I have for my career and the kind of systems I want to build.
          </p>
        </div>

        <div className="about-card glass-panel goals-card">
          <p className="about-text">
            My ambition is to establish myself in the software engineering world,
            continue building strong technical experience, and eventually create
            a fully automated sports coach. That vision combines some of the
            things I care most about: software, sports, performance, data, and
            practical tools that can help people improve. In the near term, my
            goal is to get my foot in the door as a software engineer, continue
            learning from strong teams, and grow deeper in building scalable,
            intelligent systems. Long term, I want to create technology that
            feels genuinely useful in people’s everyday lives while combining
            innovation, automation, and real-world impact.
          </p>
        </div>
      </RevealSection>
    </div>
  );
}